import AOS from 'aos';
import 'aos/dist/aos.css';
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthApiService from '../../ApiServices/AuthApiService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SigninWithGoogleRequest from "../../Models/SignInWithGoogleRequest";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import { useAuth } from "./AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import EnhancedLogo from '../Common/EnhancedLogo';

export default function Signup() {
    const navigate = useNavigate();
    let location = useLocation();
    let userInfo = location.state ?? new SigninWithGoogleRequest();

    useEffect(() => {
        AOS.init({ duration: 1000 });
    }, []);

    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState("");
    const { setAuthInfo } = useAuth();

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (password !== confirmPassword) {
            setLoading(false);
            toast.error("Passwords don't match");
            return;
        }

        try {
            let result;
            if (userInfo.UserName == null || userInfo.UserName == undefined) {
                result = await handleDefaultSignup();
            }
            toast(result.message);
            if (result.isSuccess) {
                navigate("/");
            }
        } catch (error) {
            toast.error(error.message);
            console.error('Signup error:', error);
        } finally {
            setLoading(false);
        }
    };

    async function handleDefaultSignup() {
        const signupData = {
            username: document.getElementById('first-name').value,
            email: userInfo?.UserName ?? document.getElementById('email').value,
            password: password,
            firstName: document.getElementById('first-name').value,
            lastName: document.getElementById('last-name').value,
            company: document.getElementById('company').value,
            phone: document.getElementById('phone').value,
        };
        return await AuthApiService.signup(signupData);
    }

    // Particle colors and effects
    const particleColors = [
        "rgba(255, 255, 255, 0.8)",
        "rgba(200, 200, 255, 0.6)",
        "rgba(255, 200, 255, 0.6)",
        "rgba(200, 255, 255, 0.6)",
    ];




    return (
        <div className="min-h-screen p-6 flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100">
            {/* Floating particles background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(100)].map((_, i) => {
                    const size = Math.random() * 8 + 2;
                    const duration = Math.random() * 15 + 10;
                    const delay = Math.random() * 5;
                    const color = particleColors[Math.floor(Math.random() * particleColors.length)];

                    return (
                        <motion.div
                            key={i}
                            className="absolute rounded-full"
                            style={{
                                backgroundColor: color,
                                width: `${size}px`,
                                height: `${size}px`,
                                x: Math.random() * window.innerWidth,
                                y: Math.random() * window.innerHeight,
                                filter: "blur(1px)",
                            }}
                            animate={{
                                x: [null, Math.random() * 100 - 50],
                                y: [null, Math.random() * 100 - 50],
                                opacity: [0.8, 1, 0.8],
                            }}
                            transition={{
                                duration: duration,
                                delay: delay,
                                repeat: Infinity,
                                repeatType: "reverse",
                                ease: "easeInOut",
                            }}
                        />
                    );
                })}

                {/* Large floating blobs for depth */}
                <motion.div
                    className="absolute rounded-full bg-purple-100/30 blur-3xl"
                    style={{
                        width: "400px",
                        height: "400px",
                        top: "20%",
                        left: "10%",
                    }}
                    animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.3, 0.4, 0.3]
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        repeatType: "reverse",
                        ease: "easeInOut",
                    }}
                />

                <motion.div
                    className="absolute rounded-full bg-indigo-100/30 blur-3xl"
                    style={{
                        width: "500px",
                        height: "500px",
                        bottom: "10%",
                        right: "10%",
                    }}
                    animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.3, 0.4, 0.3]
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        repeatType: "reverse",
                        ease: "easeInOut",
                    }}
                />
            </div>

            {/* Main Content */}
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="flex w-full max-w-4xl justify-center z-10"  // Changed max-width to 4xl for better form width
            >
                <motion.div
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="w-full md:w-[80%] lg:w-[70%] bg-white/95 shadow-2xl backdrop-blur-lg rounded-xl p-8 border border-white/20"  // Adjusted widths for better proportion
                >
                    <EnhancedLogo />

                    <div className="w-full mx-auto">  {/* Removed max-w-md to allow full width */}
                        <h1 className="text-3xl font-bold text-gray-800 text-center">
                            Create Your Account
                        </h1>
                        <p className="text-sm text-gray-500 font-medium mt-2 text-center">
                            Already have an account?{" "}
                            <Link
                                to="/"
                                className="text-purple-600 hover:underline font-semibold"
                            >
                                Sign In
                            </Link>
                        </p>

                        <form className="space-y-5 mt-6" onSubmit={handleSignup}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">  {/* Stack on mobile, 2 columns on desktop */}
                                <div className="w-full">  {/* Full width within column */}
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        First Name <span className="text-purple-600">*</span>
                                    </label>
                                    <input
                                        id="first-name"
                                        type="text"
                                        value={userInfo.FirstName}
                                        required
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none shadow-sm transition"
                                        placeholder="John"
                                    />
                                </div>
                                <div className="w-full">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Last Name <span className="text-purple-600">*</span>
                                    </label>
                                    <input
                                        id="last-name"
                                        type="text"
                                        required
                                        value={userInfo.LastName}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none shadow-sm transition"
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Company
                                </label>
                                <input
                                    id="company"
                                    type="text"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none shadow-sm transition"
                                    placeholder="Company Name"
                                />
                            </div>

                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email <span className="text-purple-600">*</span>
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none shadow-sm transition"
                                    placeholder="someone@gmail.com"
                                />
                            </div>

                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Phone <span className="text-purple-600">*</span>
                                </label>
                                <input
                                    id="phone"
                                    type="tel"
                                    required
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none shadow-sm transition"
                                    placeholder="123-456-7890"
                                />
                            </div>

                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Password <span className="text-purple-600">*</span>
                                </label>
                                <div className="relative w-full">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none shadow-sm transition"
                                        placeholder="••••••••••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        onClick={togglePasswordVisibility}
                                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-purple-600 transition"
                                    >
                                        <FontAwesomeIcon
                                            icon={showPassword ? faEyeSlash : faEye}
                                            className="w-5 h-5"
                                        />
                                    </button>
                                </div>
                            </div>

                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Confirm Password <span className="text-purple-600">*</span>
                                </label>
                                <div className="relative w-full">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        required
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none shadow-sm transition"
                                        placeholder="••••••••••••••••"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        onClick={toggleConfirmPasswordVisibility}
                                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-purple-600 transition"
                                    >
                                        <FontAwesomeIcon
                                            icon={showConfirmPassword ? faEyeSlash : faEye}
                                            className="w-5 h-5"
                                        />
                                    </button>
                                </div>
                            </div>

                            {/* Submit Button - Centered with full width */}
                            <div className="w-full flex justify-center">
                                <motion.button
                                    type="submit"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full md:w-3/4 lg:w-2/3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-3 rounded-lg font-medium transition-all shadow-lg"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Creating Account...
                                        </span>
                                    ) : "Sign Up"}
                                </motion.button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
}